import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Box,
  Grid,
  Dialog,
  DialogContent,
} from '@material-ui/core';
import Carousel from 'react-material-ui-carousel';
import LazyLoad from 'react-lazyload';
import '../assets/styles/DetailedHomeView.css';

const DetailedHomeView = ({ home, open, onClose, imagesHome }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const displayedImages = imagesHome?.length ? imagesHome : home?.images || [];

  // Use images directly from the passed-in home object if available
  const images = displayedImages;

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseImagePreview = () => {
    setSelectedImage(null);
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleCarouselChange = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="detailed-home-view"
      aria-describedby="detailed-view-of-the-selected-home"
      className="modal-container"
    >
      <Box className="modal-box">
        {/* Home Address */}
        <Typography variant="h4" className="home-address">
          {home?.full_address || "N/A"}
        </Typography>

        {/* MLS Information */}
        <Typography variant="body2" className="mls-info">
          <strong>MLS#:</strong> {home?.Ml_num || "N/A"}
        </Typography>

        {/* Status Information */}
        <Typography variant="body2" className="status-info">
          <strong>Status:</strong> {home?.Lsc || "N/A"} / Residential Freehold /{" "}
          {home?.Type_own1_out || "N/A"}
        </Typography>

        {/* List Price */}
        <Typography variant="body2" className="list-price">
          <strong>Price:</strong> $
          {home?.Sp_dol || home?.Lp_dol || "N/A"}
        </Typography>

        {/* Image Carousel and Thumbnails */}
        <Box className="slideshow-container">
          {images.length > 0 ? (
            <>
              {/* Image Carousel */}
              <Box className="carousel-wrapper">
                <Carousel
                  autoPlay={false}
                  navButtonsAlwaysVisible
                  className="carousel-container"
                  index={currentImageIndex}
                  onChange={handleCarouselChange}
                >
                  {images.map((image, index) => (
                    <LazyLoad key={index} height={500} offset={100} once>
                      <img
                        src={image}
                        alt={`Property Image ${index + 1}`}
                        className="carousel-image"
                        onClick={() => handleImageClick(image)}
                      />
                    </LazyLoad>
                  ))}
                </Carousel>
              </Box>

              {/* Thumbnail Preview */}
              <Box className="thumbnail-wrapper">
                <Grid
                  container
                  spacing={1}
                  className="thumbnail-grid"
                  wrap="nowrap"
                >
                  {images.map((image, index) => (
                    <Grid item key={index}>
                      <LazyLoad height={60} offset={100} once>
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className={`thumbnail-image ${
                            currentImageIndex === index ? "active-thumbnail" : ""
                          }`}
                          onClick={() => handleThumbnailClick(index)}
                        />
                      </LazyLoad>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </>
          ) : (
            // "No Images Available" View
            <Box className="no-images-container">
              <Typography variant="body2" color="textSecondary">
                No Images Available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Home Details Section */}
        <Box className="home-details-section">
          <Grid container spacing={3} className="home-attributes">
            {/* Column 1 */}
            <Grid item xs={12} sm={4}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    Style:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Style || "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    Bedrooms:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Br || "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="body2" className="label-text">
                    Bathrooms:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Bath_tot || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Column 2 */}
            <Grid item xs={12} sm={4}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    Lot Front:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Front_ft || "N/A"} ft
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    Lot Depth:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Depth || "N/A"} ft
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="body2" className="label-text">
                    Heat:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Heating || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Column 3 */}
            <Grid item xs={12} sm={4}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    A/C:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.A_c || "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" marginBottom="0px">
                  <Typography variant="body2" className="label-text">
                    Parking:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Park_spcs || "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="body2" className="label-text">
                    Taxes:
                  </Typography>
                  <Typography variant="body2" className="value-text">
                    {home?.Taxes ? `$${home.Taxes}` : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Client Remarks Section */}
        <Box mt={2} className="remarks-section">
          <Typography variant="body1" className="remarks-title">
            Client Remarks:
          </Typography>
          <Typography variant="body2" className="remarks-text">
            {home?.Ad_text || "N/A"}
          </Typography>
        </Box>

        {/* Image Preview Dialog */}
        <Dialog open={!!selectedImage} onClose={handleCloseImagePreview} maxWidth="md">
          <DialogContent>
            <img
              src={selectedImage}
              alt="Selected Property"
              className="large-image-preview"
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default DetailedHomeView;
